import { isRecord, jsonNumber, jsonText } from '../json';
import { AUTO_LOCK, PIN } from '../rcu/catalog/security';
import { STORAGE_KEY } from '../rcu/catalog/storage';
import { readStoredJson, removeStoredKey, writeStoredJson } from '../storage';
import type { StoredPin } from './pin';
import { createPin } from './pin';

/** Everything the console remembers between visits. */
export interface TechnicianState {
  readonly pin: StoredPin;
  readonly autoLockMinutes: number;
  /** Consecutive wrong PINs since the last success. */
  readonly failedAttempts: number;
  /** ISO time until which guessing is blocked, or null when open. */
  readonly lockedUntil: string | null;
}

export const isAutoLockOption = (minutes: number): boolean =>
  minutes === AUTO_LOCK.NEVER_MINUTES || AUTO_LOCK.OPTIONS_MINUTES.includes(minutes);

/**
 * First-run state: PIN `0000` and a five minute auto-lock. A fresh salt is
 * generated each call, so the stored hash differs per device.
 */
export const createDefaultTechnicianState = (): TechnicianState => ({
  pin: createPin(PIN.DEFAULT),
  autoLockMinutes: AUTO_LOCK.DEFAULT_MINUTES,
  failedAttempts: 0,
  lockedUntil: null,
});

const decodeStoredPin = (value: unknown): StoredPin | null => {
  if (!isRecord(value)) return null;
  const salt = jsonText(value.salt);
  const hash = jsonText(value.hash);
  return salt === '' || hash === '' ? null : { salt, hash };
};

/** Returns null when nothing usable is stored, so the caller can start fresh. */
export const decodeTechnicianState = (value: unknown): TechnicianState | null => {
  if (!isRecord(value)) return null;

  const pin = decodeStoredPin(value.pin);
  if (pin === null) return null;

  const autoLockMinutes = jsonNumber(value.autoLockMinutes);
  const failedAttempts = jsonNumber(value.failedAttempts);
  return {
    pin,
    autoLockMinutes:
      autoLockMinutes !== null && isAutoLockOption(autoLockMinutes)
        ? autoLockMinutes
        : AUTO_LOCK.DEFAULT_MINUTES,
    failedAttempts: failedAttempts ?? 0,
    lockedUntil: jsonText(value.lockedUntil) || null,
  };
};

const isLockedOut = (state: TechnicianState, nowMs: number): boolean =>
  state.lockedUntil !== null && Date.parse(state.lockedUntil) > nowMs;

const cleared = (state: TechnicianState): TechnicianState => ({
  ...state,
  failedAttempts: 0,
  lockedUntil: null,
});

/** Successful unlock: forget the failures. */
export const registerUnlock = (state: TechnicianState): TechnicianState => cleared(state);

/**
 * One wrong PIN. Reaching `PIN.MAX_ATTEMPTS` starts a cool-down and resets the
 * counter, so the technician gets a full set of tries afterwards.
 */
export const registerFailedAttempt = (state: TechnicianState, nowMs: number): TechnicianState => {
  const failedAttempts = state.failedAttempts + 1;
  if (failedAttempts < PIN.MAX_ATTEMPTS) return { ...state, failedAttempts };

  return {
    ...state,
    failedAttempts: 0,
    lockedUntil: new Date(nowMs + PIN.LOCKOUT_MS).toISOString(),
  };
};

/** Milliseconds left in the cool-down, or zero when guessing is allowed. */
export const lockoutRemainingMs = (state: TechnicianState, nowMs: number): number =>
  isLockedOut(state, nowMs) ? Date.parse(state.lockedUntil ?? '') - nowMs : 0;

export const loadStoredTechnicianState = (): TechnicianState => {
  const decoded = decodeTechnicianState(readStoredJson(STORAGE_KEY.TECHNICIAN));
  if (decoded === null) return createDefaultTechnicianState();
  return decoded;
};

export const saveStoredTechnicianState = (state: TechnicianState): void => {
  writeStoredJson(STORAGE_KEY.TECHNICIAN, state);
};

export const clearStoredTechnicianState = (): void => {
  removeStoredKey(STORAGE_KEY.TECHNICIAN);
};
